/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {GlobalSearchCriteriaService} from '../../services/global-search-criteria.service';
import {GlobalSearchCriteriaModel} from '../../models/global-search-criteria.model';
import {SpinnerService} from '../../../spinner/spinner.service';
import {SpinnerContextTypeEnum} from '../../../spinner/spinner-context-type.enum';
import {finalize} from 'rxjs/operators';
import {MultiSelectDropdownOptionModel} from './models/multi-select-dropdown-option.model';
import {Subscription} from 'rxjs';
import {StatefulComponent} from '../../../stateful/stateful.component';
import {InputCriteriaMultiSelectionState} from './models/input-criteria-multi-selection.state';
import {InputCriteriaTypeMapper} from '../mappers/input-criteria-type.mapper';
import {NgbDropdown} from '@ng-bootstrap/ng-bootstrap';
import {SearchCriteriaInputTypeEnum} from '../../configuration/search-criteria-input-type.enum';

@Component({
    selector: 'app-input-criteria-multi-selection',
    templateUrl: './input-criteria-multi-selection.component.html',
    styleUrls: [
        '../../shared/styles/select-dropdown.css',
        './input-criteria-multi-selection.component.css'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InputCriteriaMultiSelectionComponent extends StatefulComponent<InputCriteriaMultiSelectionState> implements OnInit, OnDestroy {
    @Input() searchCriteriaMultiSelectChange: EventEmitter<void>;
    @Input() globalSearchCriteriaModel: GlobalSearchCriteriaModel;

    @Output() selectedOptions = new EventEmitter<MultiSelectDropdownOptionModel[]>();
    @Output() searchCriteriaMultiSelectReset = new EventEmitter<void>();

    @ViewChild('dropdown') dropdown: NgbDropdown;

    private readonly LOCAL_LOADING_INSTANCE_KEY = 'DROPDOWN_CONTENT_LOADING';
    public spinnerObservable = this.spinnerService.onLocalChange(this.LOCAL_LOADING_INSTANCE_KEY);
    private subscriptions = new Subscription();

    constructor(public spinnerService: SpinnerService,
                private globalSearchService: GlobalSearchCriteriaService) {
        super();
    }

    ngOnInit(): void {
        this.fetchDropdownData();

        this.subscriptions.add(this.searchCriteriaMultiSelectChange?.subscribe(() => {
            this.clearTypedMultiSelectInput();
            // Refresh the dropdown data only when it's the same type
            if (this.globalSearchService.getSelectedSearchCriterionFilter()?.criterionFilterInputType
                    === SearchCriteriaInputTypeEnum.INPUT_MULTI_SELECTION) {
                this.fetchDropdownData();
            }
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    openChangeHandler(open: boolean): void {
        if (!open) {
            const selectedOptions = this.state.dropdownData.filter((elem) => elem.checked);
            if (selectedOptions.length) {
                this.submitMultiSelectOptions(selectedOptions);
            }
        }
    }

    checkDropdownItemHandler(option: MultiSelectDropdownOptionModel): void {
        this.state.dropdownData.forEach((item) => {
            if (option.code === item.code) {
                option.checked = !option.checked;
            }
        });
    }

    dropdownItemDataCy(itemValue: string, type: string): string {
        return itemValue && type && `dropdown-item-${type}-${itemValue.toLowerCase()
                .replace('(', '')
                .replace(')', '')
                .replace(new RegExp(/\s/g), '-')}`;
    }

    handleManuallyCloseDropdown(event: Event): void {
        event.preventDefault();
        this.dropdown.close();
    }

    private fetchDropdownData(): void {
        this.storeState({
            dropdownButtonPlaceholder: this.getDropdownButtonPlaceholder()
        });
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
        this.globalSearchCriteriaModel.dropdownDataResolverByCriterionCode.get(
                this.globalSearchService.getSelectedSearchCriterionFilter().criterion)
                ?.()
                .pipe(finalize(() => {
                    this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                }))
                .subscribe((data) => {
                    this.storeState({
                        dropdownData: data.map((elem) => ({
                            code: elem.code,
                            name: elem.name,
                            checked: false
                        }))
                    });
                });
    }

    private submitMultiSelectOptions(options: MultiSelectDropdownOptionModel[]): void {
        const queryFilter = InputCriteriaTypeMapper.fromInputCriteriaTypeToQueryChipsAreaModel(
                this.globalSearchService.getSelectedSearchCriterionFilter(),
                options.map((option) => option.name)
                        .join(' | '),
                options.map((option) => option.code));

        if (queryFilter) {
            this.globalSearchService.addCriterionFilterToQuery(queryFilter);

            this.clearTypedMultiSelectInput();
            this.searchCriteriaMultiSelectReset.emit();
        }
    }

    private clearTypedMultiSelectInput(): void {
        this.storeState({dropdownData: null, dropdownButtonPlaceholder: null});
    }

    private getDropdownButtonPlaceholder(): string {
        const dropdownData = this.globalSearchService.getDropdownDataBySearchCriteriaCode().criteria;
        const filter = this.globalSearchService.getSelectedSearchCriterionFilter();
        const criterion = dropdownData.get(filter.criterionContext)
                ?.subDropdown
                .get(filter.criterion);
        return `Select ${criterion?.name.toLowerCase()}`;
    }
}
